import { createStyles, makeStyles, Typography } from "@material-ui/core"
import { LoadingBar, ViraButton, ViraText } from "@ksana/vira-ui"
import clsx from "clsx"
import { useEffect, useRef, MouseEvent as ReactMouseEvent } from "react"
import {
  TranscriptDocument,
  TranscriptHighlight,
  TranscriptSessionStatus,
} from "../../../utils/transcriptApi"
import { colors } from "../../../utils/theme"

interface TranscriptViewProps {
  transcript: TranscriptDocument | null
  highlights: TranscriptHighlight[]
  activeSegment: number | null
  /** Upload a transcript file into this session (may replace existing). */
  onUploadTranscript?: (file: File) => void
  /** Start a recording for this session (may replace existing). */
  onRecordSession?: (event: ReactMouseEvent<HTMLElement>) => void
  isUploading?: boolean
  isRecordingBusy?: boolean
  /** Session pipeline status — error/processing must not hide Record/Upload. */
  sessionStatus?: TranscriptSessionStatus | null
  errorMessage?: string | null
}

/**
 * Renders transcript segments with score highlights and scrolls an active
 * segment into view when selected from score feedback.
 *
 * Layout matches other session tabs: intro → actions → content.
 * Record/Upload stay visible for error and in-progress sessions.
 */
export default function TranscriptView({
  transcript,
  highlights,
  activeSegment,
  onUploadTranscript,
  onRecordSession,
  isUploading = false,
  isRecordingBusy = false,
  sessionStatus = null,
  errorMessage = null,
}: TranscriptViewProps) {
  const classes = useStyles()
  const segmentRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const highlightMap = new Map<number, string>()
  const hasSegments = Boolean(transcript?.segments.length)
  const canAttach = Boolean(onUploadTranscript || onRecordSession)
  const effectiveStatus = isUploading ? "uploading" : sessionStatus
  const isPipelineBusy =
    effectiveStatus === "uploading" ||
    effectiveStatus === "transcribing" ||
    effectiveStatus === "scoring"
  const isError = effectiveStatus === "error"

  highlights.forEach((highlight) => {
    highlightMap.set(highlight.segmentRef, highlight.sentiment)
  })

  useEffect(() => {
    if (activeSegment == null) {
      return
    }
    segmentRefs.current[activeSegment]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    })
  }, [activeSegment])

  const confirmReplaceIfNeeded = () => {
    if (!hasSegments && !isError) {
      return true
    }
    return window.confirm(
      "This will replace the existing transcript or recording for this session. Continue?"
    )
  }

  const intro = isPipelineBusy
    ? effectiveStatus === "uploading"
      ? "Uploading transcript and starting processing…"
      : effectiveStatus === "transcribing"
      ? "Extracting text and labeling speakers…"
      : "Scoring the session against adherence criteria…"
    : isError
    ? "No recording or transcript for this session yet. Add one here, or write the plan and note manually."
    : hasSegments
    ? "Session transcript. Record or upload again to replace what’s here."
    : canAttach
    ? "No recording or transcript for this session yet. Add one here, or write the plan and note manually."
    : "No transcript is available for this session."

  const pipelineErrorText =
    errorMessage?.trim() ||
    "This session could not be processed. Please try again or contact support if it continues."

  return (
    <div className={classes.tabLayout}>
      <ViraText tone="muted" sx={{ fontSize: 14 }}>
        {intro}
      </ViraText>

      {canAttach ? (
        <div className={classes.emptyActions}>
          {onRecordSession ? (
            <ViraButton
              variant="primary"
              showAccentDot
              onClick={(event) => {
                if (!confirmReplaceIfNeeded()) {
                  return
                }
                onRecordSession(event)
              }}
              disabled={isRecordingBusy || isUploading || isPipelineBusy}
            >
              Record
            </ViraButton>
          ) : null}
          {onUploadTranscript ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className={classes.hiddenFileInput}
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  event.target.value = ""
                  if (!file) {
                    return
                  }
                  if (!confirmReplaceIfNeeded()) {
                    return
                  }
                  onUploadTranscript(file)
                }}
              />
              <ViraButton
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isRecordingBusy || isPipelineBusy}
                loading={isUploading}
              >
                Upload transcript
              </ViraButton>
            </>
          ) : null}
        </div>
      ) : null}

      {isPipelineBusy ? (
        <div className={classes.pipelineStatus}>
          <LoadingBar
            label={
              effectiveStatus === "uploading"
                ? "Uploading transcript…"
                : effectiveStatus === "transcribing"
                ? "Transcribing…"
                : "Scoring…"
            }
          />
        </div>
      ) : null}

      {isError ? (
        <div className={classes.errorBanner} role="alert">
          {pipelineErrorText}
        </div>
      ) : null}

      {hasSegments ? (
        <div className={classes.transcriptList}>
          {transcript!.segments.map((segment, index) => {
            const sentiment = highlightMap.get(index)
            const segmentToneClassName =
              sentiment === "strength"
                ? classes.strengthTranscriptSegment
                : sentiment === "area_for_attention"
                ? classes.attentionTranscriptSegment
                : classes.neutralTranscriptSegment

            return (
              <div
                key={`${segment.startSec}-${index}`}
                ref={(element) => {
                  segmentRefs.current[index] = element
                }}
                className={clsx(
                  classes.transcriptSegment,
                  segmentToneClassName,
                  index === activeSegment && classes.activeTranscriptSegment
                )}
              >
                <Typography
                  variant="body2"
                  className={classes.transcriptTimestamp}
                >
                  {formatTimestamp(segment.startSec)}
                </Typography>
                <div>
                  <Typography
                    variant="body2"
                    className={classes.transcriptSpeaker}
                  >
                    {segment.role || segment.speaker || "Speaker"}
                  </Typography>
                  <Typography
                    variant="body2"
                    className={classes.transcriptText}
                  >
                    {segment.text}
                  </Typography>
                </div>
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

function formatTimestamp(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = Math.round(totalSeconds % 60)
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

const useStyles = makeStyles((theme) =>
  createStyles({
    tabLayout: {
      display: "grid",
      gap: theme.spacing(2),
      justifyItems: "start",
      width: "100%",
    },
    emptyActions: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1.5),
      alignItems: "center",
    },
    pipelineStatus: {
      width: "100%",
      justifySelf: "stretch",
    },
    errorBanner: {
      width: "100%",
      justifySelf: "stretch",
      padding: theme.spacing(2),
      borderRadius: 4,
      backgroundColor: colors.secondary.blush,
      color: colors.secondary.crimson,
      fontSize: 14,
      lineHeight: 1.5,
      fontWeight: 400,
    },
    hiddenFileInput: {
      display: "none",
    },
    activeTranscriptSegment: {
      backgroundColor: "var(--vira-soft-surface, #E8EEF9)",
      boxShadow: "inset 0 0 0 1px var(--vira-border, #C5CBD8)",
    },
    transcriptList: {
      display: "grid",
      gap: theme.spacing(1.5),
      width: "100%",
      // Let LoopShell main own vertical scroll — nested maxHeight + overflow
      // steals the first wheel gesture.
      maxHeight: "none",
      overflowY: "visible",
    },
    transcriptSegment: {
      display: "grid",
      gridTemplateColumns: "64px 1fr",
      gap: theme.spacing(2),
      padding: theme.spacing(2),
      borderRadius: 4,
      borderLeft: "4px solid transparent",
      backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
      border: `1px solid var(--vira-border, ${colors.neutral.clay})`,
    },
    neutralTranscriptSegment: {},
    strengthTranscriptSegment: {
      borderLeftColor: colors.graphs.alerts.positive,
      backgroundColor: "var(--vira-soft-surface, #F4FCF6)",
    },
    attentionTranscriptSegment: {
      borderLeftColor: colors.tertiary.tangerine,
      backgroundColor: "var(--vira-soft-surface, #FFF8ED)",
    },
    transcriptTimestamp: {
      color: "var(--vira-muted, #5C6578)",
      fontFamily: "monospace",
    },
    transcriptSpeaker: {
      color: "var(--vira-ink, #1E263C)",
      fontWeight: 700,
      marginBottom: theme.spacing(0.5),
    },
    transcriptText: {
      color: "var(--vira-ink, #1E263C)",
      whiteSpace: "pre-wrap",
    },
  })
)
